/**
 * Composable singleton du mode maintenance (SD-4367) — copie locale GGVie (ADR-019).
 *
 * État module-level partagé par le router, App.vue et les vues : un seul polling
 * pour toute l'app, celui de l'état du service (SD-4692, `service/useServiceStatus.ts`). Le statut effectif est recalculé localement (offset
 * horloge serveur + tick 1 s pendant un décompte) : à 0:00 la bascule se fait
 * SANS attendre le prochain poll.
 *
 * Résilience deploy (SD-4352, pas de zero-downtime) : un échec réseau ou une
 * réponse non-200 ne modifie JAMAIS l'état — seul un 200 `status:"off"` éteint
 * le mode. Le dernier état connu est persisté en localStorage pour ré-afficher
 * la page maintenance immédiatement après un rechargement pendant que le
 * backend est down.
 */
import { computed, ref, type ComputedRef, type Ref } from 'vue'

import { authFetch, onMaintenanceDetected } from '@ggvie-consulting/auth/api'
import config from '@ggvie-consulting/config'
import type { ServiceSnapshot } from '@ggvie-consulting/service/logic'
import {
  onServiceSnapshot,
  refreshServiceStatus,
  startServicePolling,
  stopServicePolling,
} from '@ggvie-consulting/service/useServiceStatus'

import {
  computeRemainingSeconds,
  computeServerOffsetMs,
  effectiveStatus,
  type MaintenanceStatus,
  type MaintenanceStatusPayload,
} from './logic'
import { STORAGE_LAST, STORAGE_RETURN_TO } from './storageKeys'

function readStoredPayload(): MaintenanceStatusPayload | null {
  try {
    const raw = localStorage.getItem(STORAGE_LAST)
    return raw ? (JSON.parse(raw) as MaintenanceStatusPayload) : null
  } catch {
    return null
  }
}

function persistPayload(payload: MaintenanceStatusPayload): void {
  try {
    localStorage.setItem(STORAGE_LAST, JSON.stringify(payload))
  } catch {
    /* stockage indisponible (mode privé, quota) : l'état mémoire suffit */
  }
}

const rawPayload: Ref<MaintenanceStatusPayload | null> = ref(readStoredPayload())
const serverOffsetMs = ref(0)
// Tick 1 s pendant un `scheduled` : force la réévaluation du statut effectif et
// du décompte sans aller-retour réseau.
const nowTick = ref(Date.now())

const status: ComputedRef<MaintenanceStatus> = computed(() => {
  const payload = rawPayload.value
  return payload ? effectiveStatus(payload, nowTick.value + serverOffsetMs.value) : 'off'
})

const remainingSeconds: ComputedRef<number | null> = computed(() => {
  const payload = rawPayload.value
  if (!payload || payload.status !== 'scheduled') return null
  return computeRemainingSeconds(payload.activates_at, nowTick.value + serverOffsetMs.value)
})

let tickTimer: ReturnType<typeof setInterval> | null = null
let unsubscribe: (() => void) | null = null
let loadPromise: Promise<void> | null = null

function syncTick(): void {
  const needTick = rawPayload.value?.status === 'scheduled'
  if (needTick && tickTimer === null) {
    tickTimer = setInterval(() => {
      nowTick.value = Date.now()
    }, 1000)
  } else if (!needTick && tickTimer !== null) {
    clearInterval(tickTimer)
    tickTimer = null
  }
}

function applyPayload(payload: MaintenanceStatusPayload): void {
  rawPayload.value = payload
  serverOffsetMs.value = computeServerOffsetMs(payload.server_time, Date.now())
  nowTick.value = Date.now()
  persistPayload(payload)
  syncTick()
}

// SD-4692 — un seul sondage par onglet (`/service/status`) : « off » y suffit ; sinon le détail (échéance) est relu.
async function syncFromService(service: ServiceSnapshot | null): Promise<void> {
  if (service && service.maintenance === 'off') {
    applyPayload({ status: 'off', activates_at: null, server_time: service.server_time ?? new Date().toISOString() })
    return
  }
  const response = await authFetch(`${config.api.baseURL}/v1/maintenance/status`, { skipAuth: true })
  // Non-200 (5xx nginx pendant un deploy, etc.) : on CONSERVE le dernier état connu.
  if (!response.ok) return
  applyPayload((await response.json()) as MaintenanceStatusPayload)
}

async function refreshNow(): Promise<void> {
  await syncFromService(await refreshServiceStatus())
}

/** Premier chargement memoizé — ne rejette JAMAIS (un throw dans beforeEach = app blanche). */
function ensureLoaded(): Promise<void> {
  if (!loadPromise) {
    loadPromise = refreshNow().catch(() => undefined)
  }
  return loadPromise
}

function startPolling(): void {
  if (unsubscribe !== null) return
  void ensureLoaded()
  unsubscribe = onServiceSnapshot((service) => {
    void syncFromService(service).catch(() => undefined)
  })
  startServicePolling()
}

function stopPolling(): void {
  if (unsubscribe !== null) {
    unsubscribe()
    unsubscribe = null
  }
  stopServicePolling()
}

function saveReturnTo(fullPath: string): void {
  try {
    localStorage.setItem(STORAGE_RETURN_TO, fullPath)
  } catch {
    /* stockage indisponible : on retombera sur la home */
  }
}

function consumeReturnTo(): string | null {
  try {
    const value = localStorage.getItem(STORAGE_RETURN_TO)
    localStorage.removeItem(STORAGE_RETURN_TO)
    return value
  } catch {
    return null
  }
}

// Bascule immédiate quand un appel API quelconque reçoit le 503-marqueur : on force
// `active` localement puis on resynchronise avec l'endpoint de vérité.
onMaintenanceDetected(() => {
  const forced: MaintenanceStatusPayload = {
    status: 'active',
    activates_at: null,
    server_time: new Date(Date.now() + serverOffsetMs.value).toISOString(),
  }
  rawPayload.value = forced
  persistPayload(forced)
  syncTick()
  void refreshNow().catch(() => undefined)
})

// Rechargement pendant un décompte hydraté du localStorage : relance le tick.
syncTick()

export interface UseMaintenance {
  status: ComputedRef<MaintenanceStatus>
  remainingSeconds: ComputedRef<number | null>
  ensureLoaded: () => Promise<void>
  startPolling: () => void
  stopPolling: () => void
  refreshNow: () => Promise<void>
  saveReturnTo: (fullPath: string) => void
  consumeReturnTo: () => string | null
}

const singleton: UseMaintenance = {
  status,
  remainingSeconds,
  ensureLoaded,
  startPolling,
  stopPolling,
  refreshNow,
  saveReturnTo,
  consumeReturnTo,
}

export function useMaintenance(): UseMaintenance {
  return singleton
}
